import authStore from '../contexts/authStore';
import { broadcastAuthEvent } from './authBroadcast';
import { isUserActive, wasActiveWithin } from './userActivity';
import { getConfig } from "../config/runtimeConfig";

/**
 * Minimal token expiration utility
 * Only handles checking if token is expired beyond the configured threshold
 */

/**
 * Decode JWT token payload (client-side only, no verification)
 */
const decodeTokenPayload = (token: string): any | null => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Check if token is expired beyond the configured threshold
 * @param token - JWT token string (optional, will get from localStorage if not provided)
 * @returns boolean - true if token is expired beyond threshold and user should be redirected
 */
export const isTokenExpiredBeyondThreshold = (token?: string): boolean => {
  const tokenToCheck = token || authStore.getToken();

  if (!tokenToCheck) {
    return true; // No token means user should be redirected (but caller should handle this appropriately)
  }

  const decoded = decodeTokenPayload(tokenToCheck);
  if (!decoded || !decoded.exp) {
    return true; // Invalid token means user should be redirected
  }

  const now = Math.floor(Date.now() / 1000);
  const tokenExpiry = decoded.exp;
  const isExpired = now > tokenExpiry;

  if (!isExpired) {
    return false; // Token is still valid
  }

  // Token is expired, check if it's beyond the threshold
  const expiredForSeconds = now - tokenExpiry;
  const thresholdMinutes = parseInt(
    getConfig("REACT_APP_TOKEN_EXPIRY_LOGOUT_THRESHOLD", '2')
  );
  const thresholdSeconds = thresholdMinutes * 60;

  return expiredForSeconds > thresholdSeconds;
};

/**
 * Check if token is expired but within the refresh threshold
 * @param token - JWT token string (optional, will get from localStorage if not provided)
 * @returns boolean - true if token is expired but can be refreshed
 */
export const isTokenExpiredWithinThreshold = (token?: string): boolean => {
  const tokenToCheck = token || authStore.getToken();

  if (!tokenToCheck) {
    return false; // No token means nothing to refresh
  }

  const decoded = decodeTokenPayload(tokenToCheck);
  if (!decoded || !decoded.exp) {
    return false; // Invalid token can't be refreshed
  }

  const now = Math.floor(Date.now() / 1000);
  const tokenExpiry = decoded.exp;
  const isExpired = now > tokenExpiry;

  if (!isExpired) {
    return false; // Token is still valid, no need to refresh
  }

  // Token is expired, check if it's within the refresh threshold
  const expiredForSeconds = now - tokenExpiry;
  const thresholdMinutes = parseInt(
    getConfig("REACT_APP_TOKEN_EXPIRY_LOGOUT_THRESHOLD", '2')
  );
  const thresholdSeconds = thresholdMinutes * 60;

  return expiredForSeconds <= thresholdSeconds;
};

/**
 * Check if token will expire soon and needs proactive refresh
 * @param token - JWT token string (optional, will get from localStorage if not provided)
 * @param refreshThresholdMinutes - Minutes before expiry to trigger refresh (default: 2)
 * @returns boolean - true if token will expire within the threshold and should be refreshed proactively
 */
export const isTokenExpiringSoon = (token?: string, refreshThresholdMinutes?: number): boolean => {
  const tokenToCheck = token || authStore.getToken();

  if (!tokenToCheck) {
    return false; // No token means nothing to refresh
  }

  const decoded = decodeTokenPayload(tokenToCheck);
  if (!decoded || !decoded.exp) {
    return false; // Invalid token can't be refreshed
  }

  const now = Math.floor(Date.now() / 1000);
  const tokenExpiry = decoded.exp;

  // If token is already expired, don't consider it as "expiring soon"
  // Let the existing expired token logic handle it
  if (now > tokenExpiry) {
    return false;
  }

  // Check if token will expire within the proactive refresh threshold
  const thresholdMinutes = refreshThresholdMinutes ?? parseInt(
    getConfig("REACT_APP_PROACTIVE_REFRESH_THRESHOLD", '2')
  );
  const thresholdSeconds = thresholdMinutes * 60;
  const timeUntilExpiry = tokenExpiry - now;

  return timeUntilExpiry <= thresholdSeconds;
};

/**
 * Check if token is about to expire and should show warning modal
 * @param token - JWT token string (optional, will get from localStorage if not provided)
 * @param warningThresholdMinutes - Minutes before expiry to show warning (default: 1)
 * @returns boolean - true if token will expire within the warning threshold
 */
export const isTokenExpiringSoonForWarning = (token?: string, warningThresholdMinutes?: number): boolean => {
  const tokenToCheck = token || authStore.getToken();

  if (!tokenToCheck) {
    return false; // No token means nothing to warn about
  }

  const decoded = decodeTokenPayload(tokenToCheck);
  if (!decoded || !decoded.exp) {
    return false; // Invalid token can't be warned about
  }

  const now = Math.floor(Date.now() / 1000);
  const tokenExpiry = decoded.exp;

  // If token is already expired, don't show warning (show expired modal instead)
  if (now > tokenExpiry) {
    return false;
  }

  // Check if token will expire within the warning threshold
  const thresholdMinutes = warningThresholdMinutes ?? parseInt(
    getConfig("REACT_APP_SESSION_WARNING_THRESHOLD", '1')
  );
  const thresholdSeconds = thresholdMinutes * 60;
  const timeUntilExpiry = tokenExpiry - now;

  return timeUntilExpiry <= thresholdSeconds;
};

/**
 * Check if we should show warning modal instead of expired modal
 * This ensures warning modal always appears before expired modal
 * @param token - JWT token string (optional, will get from localStorage if not provided)
 * @returns boolean - true if we should show warning modal instead of expired modal
 */
export const shouldShowWarningInsteadOfExpired = (token?: string): boolean => {
  const tokenToCheck = token || authStore.getToken();

  if (!tokenToCheck) {
    return false; // No token means show expired modal
  }

  const decoded = decodeTokenPayload(tokenToCheck);
  if (!decoded || !decoded.exp) {
    return false; // Invalid token means show expired modal
  }

  const now = Math.floor(Date.now() / 1000);
  const tokenExpiry = decoded.exp;

  // If token is not expired yet, check if it's within warning threshold
  if (now <= tokenExpiry) {
    return isTokenExpiringSoonForWarning(tokenToCheck);
  }

  // Token is expired - check if it's still within the warning grace period
  // This allows showing warning modal even for recently expired tokens
  const expiredForSeconds = now - tokenExpiry;
  const warningThresholdMinutes = parseInt(
    getConfig("REACT_APP_SESSION_WARNING_THRESHOLD", '1')
  );
  const warningGracePeriodSeconds = warningThresholdMinutes * 60;

  // Show warning if token expired less than warning threshold ago
  return expiredForSeconds <= warningGracePeriodSeconds;
};

/**
 * Get remaining seconds until token expires
 * @param token - JWT token string (optional, will get from localStorage if not provided)
 * @returns number - seconds until expiry, or 0 if expired/invalid
 */
export const getRemainingTokenSeconds = (token?: string): number => {
  const tokenToCheck = token || authStore.getToken();

  if (!tokenToCheck) {
    return 0;
  }

  const decoded = decodeTokenPayload(tokenToCheck);
  if (!decoded || !decoded.exp) {
    return 0;
  }

  const now = Math.floor(Date.now() / 1000);
  const tokenExpiry = decoded.exp;
  const timeUntilExpiry = tokenExpiry - now;
  const result = Math.max(0, timeUntilExpiry);

  return result;
};

/**
 * Get the warning countdown duration in seconds
 * This is the time the user has to respond to the warning modal
 * @returns number - warning threshold duration in seconds
 */
export const getWarningCountdownSeconds = (): number => {
  const warningThresholdMinutes = parseFloat(
    getConfig("REACT_APP_SESSION_WARNING_THRESHOLD", '1')
  );
  const warningCountdownSeconds = warningThresholdMinutes * 60;

  return warningCountdownSeconds;
};

/**
 * Check if user should get proactive token refresh (for continuously active users)
 * Only truly active users (working continuously) get seamless token refresh
 * Users who just became active should see the warning modal instead
 * @param token - JWT token string (optional, will get from authStore if not provided)
 * @returns boolean - true if user has been continuously active and token should be refreshed proactively
 */
export const shouldProactivelyRefreshForActiveUser = (token?: string): boolean => {
  const tokenToCheck = token || authStore.getToken();

  if (!tokenToCheck) {
    return false;
  }

  // Check if user is currently active
  if (!isUserActive()) {
    return false;
  }

  // Check if user has been active for at least 5 minutes continuously
  // This prevents auto-refresh for users who just became active
  const continuousActivityThreshold = 5 * 60 * 1000; // 5 minutes
  if (!wasActiveWithin(continuousActivityThreshold)) {
    return false;
  }

  // Check if token is expired or expiring soon
  const decoded = decodeTokenPayload(tokenToCheck);
  if (!decoded || !decoded.exp) {
    return false;
  }

  const now = Math.floor(Date.now() / 1000);
  const tokenExpiry = decoded.exp;
  const timeUntilExpiry = tokenExpiry - now;

  // For continuously active users, refresh token if it's expired or will expire within 30 seconds
  const proactiveRefreshThreshold = 30; // 30 seconds


  return timeUntilExpiry <= proactiveRefreshThreshold;
};

/**
 * Check if user should see warning modal (for idle users)
 * Idle users get warning modal before session expires
 * @param token - JWT token string (optional, will get from authStore if not provided)
 * @returns boolean - true if idle user should see warning modal
 */
export const shouldShowWarningForIdleUser = (token?: string): boolean => {
  const tokenToCheck = token || authStore.getToken();

  if (!tokenToCheck) {
    return false;
  }

  // Only show warning for idle users
  if (isUserActive()) {
    return false;
  }

  const decoded = decodeTokenPayload(tokenToCheck);
  if (!decoded || !decoded.exp) {
    return false;
  }

  const now = Math.floor(Date.now() / 1000);
  const tokenExpiry = decoded.exp;
  const logoutThresholdMinutes = parseInt(
    getConfig("REACT_APP_TOKEN_EXPIRY_LOGOUT_THRESHOLD", '2')
  );
  const warningThresholdMinutes = parseFloat(
    getConfig("REACT_APP_SESSION_WARNING_THRESHOLD", '1')
  );

  // Calculate when to show warning: token_expiry + logout_threshold - warning_threshold
  const logoutThresholdSeconds = logoutThresholdMinutes * 60;
  const warningThresholdSeconds = warningThresholdMinutes * 60;
  const warningTime = tokenExpiry + logoutThresholdSeconds - warningThresholdSeconds;

  return now >= warningTime;
};

/**
 * Check if session should be terminated (final logout)
 * This happens when token is expired beyond the logout threshold
 * @param token - JWT token string (optional, will get from authStore if not provided)
 * @returns boolean - true if session should be terminated
 */
export const shouldTerminateSession = (token?: string): boolean => {
  const tokenToCheck = token || authStore.getToken();

  if (!tokenToCheck) {
    return true; // No token means session should be terminated
  }

  const decoded = decodeTokenPayload(tokenToCheck);
  if (!decoded || !decoded.exp) {
    return true; // Invalid token means session should be terminated
  }

  const now = Math.floor(Date.now() / 1000);
  const tokenExpiry = decoded.exp;
  const logoutThresholdMinutes = parseInt(
    getConfig("REACT_APP_TOKEN_EXPIRY_LOGOUT_THRESHOLD", '2')
  );
  const logoutThresholdSeconds = logoutThresholdMinutes * 60;

  // Session should be terminated if current time is past token_expiry + logout_threshold
  const terminationTime = tokenExpiry + logoutThresholdSeconds;

  return now >= terminationTime;
};

/**
 * Clean up expired token and related data from localStorage
 * This prevents infinite redirect loops by removing expired tokens
 * and ensures proper layout selection after page reload
 */
export const cleanupExpiredToken = (): void => {

  // Remove all authentication-related data
  authStore.clearTokens();
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userId');
  localStorage.removeItem('userInfo');
  localStorage.removeItem('notifications');

  // Save current path for redirect after login (per-tab)
  try {
    sessionStorage.setItem('lastVisitedPath', window.location.pathname);
  } catch {}

};

/**
 * Attempt to refresh an expired token automatically
 * @returns Promise<boolean> - true if refresh was successful, false otherwise
 */
export const attemptTokenRefresh = async (): Promise<boolean> => {
  try {

    const currentRefreshToken = authStore.getRefreshToken();
    if (!currentRefreshToken) {
      return false;
    }

    // Import refreshToken function dynamically to avoid circular imports
    const { refreshToken } = await import('../services/authService');

    const result = await refreshToken({
      refresh_token: currentRefreshToken,
    });

    if (result?.access_token && result?.refresh_token) {
      // Keep global store in sync with persistence - store both tokens
      authStore.setTokens(result.access_token, result.refresh_token);

      // Broadcast token refresh event to other tabs
      broadcastAuthEvent('token-refreshed');

      return true;
    } else {
      return false;
    }
  } catch (error: any) {
    console.error('❌ Token refresh failed:', error);

    // Check if refresh token API returned 401 (unauthorized)
    if (error?.response?.status === 401) {
      // Refresh token is invalid/expired - redirect to login
      cleanupExpiredToken();
      window.location.href = '/login';
    }

    return false;
  }
};

/**
 * Get token expiration info for debugging
 */
export const getTokenExpirationInfo = (token?: string): any => {
  const tokenToCheck = token || authStore.getToken();

  if (!tokenToCheck) {
    return { hasToken: false };
  }

  const decoded = decodeTokenPayload(tokenToCheck);
  if (!decoded || !decoded.exp) {
    return { hasToken: true, isValid: false };
  }

  const now = Math.floor(Date.now() / 1000);
  const tokenExpiry = decoded.exp;
  const isExpired = now > tokenExpiry;
  const expiredForSeconds = isExpired ? now - tokenExpiry : 0;
  const expiredForMinutes = expiredForSeconds / 60;
  const timeUntilExpirySeconds = isExpired ? 0 : tokenExpiry - now;
  const timeUntilExpiryMinutes = timeUntilExpirySeconds / 60;

  const logoutThresholdMinutes = parseInt(
    getConfig("REACT_APP_TOKEN_EXPIRY_LOGOUT_THRESHOLD", '2')
  );

  const proactiveRefreshThresholdMinutes = parseInt(
    getConfig("REACT_APP_PROACTIVE_REFRESH_THRESHOLD", '2')
  );

  return {
    hasToken: true,
    isValid: !isExpired,
    isExpired,
    expiredForMinutes: Math.round(expiredForMinutes * 100) / 100,
    timeUntilExpiryMinutes: Math.round(timeUntilExpiryMinutes * 100) / 100,
    logoutThresholdMinutes,
    proactiveRefreshThresholdMinutes,
    shouldRedirect: isTokenExpiredBeyondThreshold(tokenToCheck),
    shouldProactiveRefresh: isTokenExpiringSoon(tokenToCheck),
    expiresAt: new Date(tokenExpiry * 1000).toISOString()
  };
};


